import React, { useRef, useEffect, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItemButton, ListItemIcon, ListItemText, Stack, TextField, Typography } from "@mui/material";
import { FirebaseAuth } from "../../FirebaseAuth/firebase";
import { AuthContext } from "../../FirebaseAuth";
import { userSignOut } from '../../../libs/user';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import HistoryIcon from "@mui/icons-material/History";
import SecurityIcon from "@mui/icons-material/Security";

const UserMenu = ({ onAction }) => {

    const mountedRef = useRef(true);
    const history = useHistory();
    const { authUser } = useContext(AuthContext);
    const roleLabel = String(authUser?.role || "user");
    const isClientRole = roleLabel.toLowerCase() === "client";
    const displayName = FirebaseAuth.auth().currentUser?.displayName || "User";
    const email = FirebaseAuth.auth().currentUser?.email || "";
    const photoURL = FirebaseAuth.auth().currentUser?.photoURL || "";
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteBusy, setInviteBusy] = useState(false);
    const [inviteToken, setInviteToken] = useState("");
    const [inviteLink, setInviteLink] = useState("");
    const [inviteExpiry, setInviteExpiry] = useState("");
    const [inviteError, setInviteError] = useState("");
    const normalizedInviteEmail = String(inviteEmail || "").trim().toLowerCase();
    const inviteEmailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedInviteEmail);
    // const [loading, setLoading] = useState(true);
    // const [accounts, setAccounts] = useState([]);
    
    // const getAccounts = () => {
    //     setLoading(true);
    //     let records = [];
    //     const accountsRef = FirebaseAuth.firestore().collection('accounts');
    //     let query = accountsRef.where('access', 'array-contains', FirebaseAuth.auth().currentUser.uid);
    //     query.get().then(accountSnapshots => {
    //         if (!mountedRef.current) return null
    //         accountSnapshots.forEach(account => {
    //             records.push({
    //                 'id': account.id,
    //                 'path': '/account/'+account.id
    //             });
    //         });
    //         setAccounts(records);
    //         setLoading(false);
    //     });
    // }

    useEffect(() => {
        return () => { 
            mountedRef.current = false
        }
    },[]);

    const goTo = (path) => {
        history.push(path);
        onAction?.();
    };

    const handleGenerateInvite = async () => {
        if (!inviteEmailLooksValid) {
            setInviteError("A valid invitee email is required.");
            return;
        }
        setInviteBusy(true);
        setInviteError("");
        try {
            const generateInvite = FirebaseAuth.functions().httpsCallable("generateClientAccountInvite");
            const response = await generateInvite({
                email: normalizedInviteEmail,
                app_base_url: window.location.origin,
            });
            const data = response?.data || {};
            setInviteToken(String(data.invite_token || ""));
            setInviteLink(String(data.invite_link || ""));
            setInviteExpiry(String(data.expires_at || ""));
        } catch (error) {
            setInviteError(String(error?.message || "Failed to generate invite."));
        } finally {
            setInviteBusy(false);
        }
    };

    return (
        <Box sx={{ width: "100%", maxHeight: "inherit", bgcolor: "#f8fafd", display: "flex", flexDirection: "column", overflowY: "auto" }}>
            <Box
                sx={{
                    bgcolor: "#fff",
                    borderBottom: "1px solid #dadce0",
                    p: 1.75,
                }}
            >
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                        <Avatar
                            alt={displayName}
                            src={photoURL}
                            sx={{ width: 44, height: 44 }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, textAlign: "left", lineHeight: 1.2 }} noWrap>
                                {displayName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "left" }} noWrap>
                                {email}
                            </Typography>
                        </Box>
                        <Chip
                            size="small"
                            label={roleLabel.charAt(0).toUpperCase() + roleLabel.slice(1)}
                            color="info"
                            variant="outlined"
                        />
                    </Box>
            </Box>

            <Stack spacing={1.5} sx={{ p: 1.5, flex: 1, minHeight: 0 }}>
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800, letterSpacing: 0.7, mb: 0.75, px: 0.5, textAlign: "left", textTransform: "uppercase" }}>
                        Account
                    </Typography>
                    <List sx={{ bgcolor: "#fff", border: "1px solid #dadce0", borderRadius: 2, overflow: "hidden", p: 0 }}>
                        <ListItemButton sx={{ px: 1.5, py: 1.15 }} onClick={(e) => {
                            e.preventDefault();
                            goTo("/user/profile");
                        }}>
                            <ListItemIcon sx={{ minWidth: 38 }}><EditNoteIcon sx={{ fontSize: 20 }} color="info" /></ListItemIcon>
                            <ListItemText
                                primary="Profile"
                                secondary="Manage identity and work profile"
                                primaryTypographyProps={{ fontWeight: 700 }}
                                secondaryTypographyProps={{ fontSize: 13 }}
                            />
                        </ListItemButton>
                        <Divider />
                        <ListItemButton sx={{ px: 1.5, py: 1.15 }} onClick={(e) => {
                            e.preventDefault();
                            goTo("/user/security");
                        }}>
                            <ListItemIcon sx={{ minWidth: 38 }}><SecurityIcon sx={{ fontSize: 20 }} color="success" /></ListItemIcon>
                            <ListItemText
                                primary="Security"
                                secondary="Manage password, MFA, and account controls"
                                primaryTypographyProps={{ fontWeight: 700 }}
                                secondaryTypographyProps={{ fontSize: 13 }}
                            />
                        </ListItemButton>
                        <Divider />
                        <ListItemButton sx={{ px: 1.5, py: 1.15 }} onClick={(e) => {
                            e.preventDefault();
                            goTo("/user/log");
                        }}>
                            <ListItemIcon sx={{ minWidth: 38 }}><HistoryIcon sx={{ fontSize: 20 }} color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="Audit Logs"
                                secondary="Review account event history"
                                primaryTypographyProps={{ fontWeight: 700 }}
                                secondaryTypographyProps={{ fontSize: 13 }}
                            />
                        </ListItemButton>

                        {isClientRole && (
                            <>
                                <Divider />
                                <ListItemButton sx={{ px: 1.5, py: 1.15 }} onClick={(e) => {
                                    e.preventDefault();
                                    setInviteOpen(true);
                                }}>
                                    <ListItemIcon sx={{ minWidth: 38 }}><PersonAddAlt1Icon sx={{ fontSize: 20 }} color="secondary" /></ListItemIcon>
                                    <ListItemText
                                        primary="Send Invite"
                                        secondary="Invite another user"
                                        primaryTypographyProps={{ fontWeight: 700 }}
                                        secondaryTypographyProps={{ fontSize: 13 }}
                                    />
                                </ListItemButton>
                            </>
                        )}
                    </List>
                </Box>

                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800, letterSpacing: 0.7, mb: 0.75, px: 0.5, textAlign: "left", textTransform: "uppercase" }}>
                        Session
                    </Typography>
                    <List sx={{ bgcolor: "#fff", border: "1px solid #dadce0", borderRadius: 2, overflow: "hidden", p: 0 }}>
                        <ListItemButton sx={{ px: 1.5, py: 1.15 }} onClick={(e) => {
                            e.preventDefault();
                            onAction?.();
                            userSignOut();
                        }}>
                            <ListItemIcon sx={{ minWidth: 38 }}><PowerSettingsNewIcon sx={{ fontSize: 20 }} color="error" /></ListItemIcon>
                            <ListItemText
                                primary="Sign Out"
                                secondary="End this browser session"
                                primaryTypographyProps={{ fontWeight: 700 }}
                                secondaryTypographyProps={{ fontSize: 13 }}
                            />
                        </ListItemButton>
                    </List>
                </Box>
            </Stack>
            <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Send Invite</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        fullWidth
                        size="small"
                        label="Invitee email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        error={Boolean(inviteEmail) && !inviteEmailLooksValid}
                        helperText={Boolean(inviteEmail) && !inviteEmailLooksValid ? "Enter a valid email address." : ""}
                        sx={{ mb: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        Enter the user's email address, and generate the invite, then share the invite link below with that user.
                    </Typography>
                    {inviteError && (
                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                            {inviteError}
                        </Typography>
                    )}
                    {!!inviteToken && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Invite Token</Typography>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all", mb: 1 }}>
                                {inviteToken}
                            </Typography>
                            {!!inviteLink && (
                                <>
                                    <Typography variant="caption" color="text.secondary">Invite Link</Typography>
                                    <Typography variant="body2" sx={{ wordBreak: "break-all", mb: 1 }}>
                                        {inviteLink}
                                    </Typography>
                                </>
                            )}
                            {!!inviteExpiry && (
                                <Typography variant="caption" color="text.secondary">
                                    Expires at: {inviteExpiry}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={() => setInviteOpen(false)}
                        sx={{ textTransform: "none" }}
                    >
                        Close
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleGenerateInvite}
                        disabled={inviteBusy || !inviteEmailLooksValid}
                        sx={{ textTransform: "none" }}
                    >
                        {inviteBusy ? "Generating..." : "Generate Invite"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
        
    )
}

export default UserMenu;
